import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {
  SchedulerCalendar,
  SchedulerTemplateForm,
} from 'src/types/scheduler/template-form';
import type {AgentSettings} from 'src/types/scheduler/settings';
import type {BookingAccount} from 'src/types/scheduler/templates';

import {camel, snake} from 'src/utils';
import omit from 'lodash/omit';
import uniqueId from 'lodash/uniqueId';
import * as reduxApi from 'src/utils/redux-api-v2';

import {initialState} from 'src/reducers/scheduler/template-form';
import {
  updateOrgTemplate,
  saveWorkingAssignments,
} from 'src/action-creators/scheduler/org-templates';
import {updateTemplate} from 'src/action-creators/scheduler/templates';
import {selectAgentApiParams} from 'src/selectors/scheduler/global';
import {selectTemplateForm} from 'src/selectors/scheduler/template-form';
import {selectAgencyData} from 'src/selectors/agency';
import {bookingRowsAsBookingSlots} from 'src/components/scheduler/editor/reducer';
import {
  normalizeCapacity,
  normalizeBookingAccounts,
} from 'src/components/scheduler/template-editor/helper-functions';
import {
  MULTI_EVENT_TEMPLATE_TYPE,
  TEMPLATE_FORM_PUT_BODY_OMIT_KEYS,
} from 'src/components/scheduler/template-editor/constants';
import {trackTemplateSaveSuccess} from 'src/components/scheduler/analytics/scheduler-amplitude-track';
import {setTemplateFormAnalyticsState} from 'src/components/scheduler/analytics/template-form-analytics-state';


export const SET_TEMPLATE_FORM = 'scheduling/templateForm/setTemplateForm';
export const UPDATE_TEMPLATE_FORM =
  'scheduling/templateForm/updateTemplateFormField';
export const UPDATE_TEMPLATE_FORM_FIELDS =
  'scheduling/templateForm/updateTemplateFormFields';

export type SchedulerTemplateFormActions =
  | SetTemplateFormAction
  | UpdateTemplateFormFieldAction
  | UpdateTemplateFormFieldsAction;

// set template form action
export type SetTemplateFormAction = {
  type: typeof SET_TEMPLATE_FORM;
  payload: SchedulerTemplateForm;
};

export const setTemplateForm = (
  payload: SchedulerTemplateForm,
): SetTemplateFormAction => {
  const serializedPayload = parseTemplateForm(payload);
  return {
    type: SET_TEMPLATE_FORM,
    payload: serializedPayload,
  };
};

// update template form action
type UpdateTemplateFormFieldPayload = {
  field: string;
  value: unknown;
};
export type UpdateTemplateFormFieldAction = {
  type: typeof UPDATE_TEMPLATE_FORM;
  payload: UpdateTemplateFormFieldPayload;
};
export const updateTemplateFormField = (
  payload: UpdateTemplateFormFieldPayload,
): UpdateTemplateFormFieldAction => ({
  type: UPDATE_TEMPLATE_FORM,
  payload,
});

// update multiple template form fields action
type UpdateTemplateFormFieldsPayload = {
  [field: string]: unknown;
};
export type UpdateTemplateFormFieldsAction = {
  type: typeof UPDATE_TEMPLATE_FORM_FIELDS;
  payload: UpdateTemplateFormFieldsPayload;
};
export const updateTemplateFormFields = (
  payload: UpdateTemplateFormFieldsPayload,
): UpdateTemplateFormFieldsAction => ({
  type: UPDATE_TEMPLATE_FORM_FIELDS,
  payload,
});

export const fetchTemplate =
  (templateId: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const {actor} = selectAgentApiParams(state);
    try {
      const result = await dispatch(
        reduxApi.get(`scheduler/event-template/${templateId}`, {actor}),
      );

      const camelizedResult = camel(result, true, ['blocks', 'entityMap']);
      dispatch(setTemplateForm(camelizedResult));
      setTemplateFormAnalyticsState(parseTemplateForm(camelizedResult));
    } catch (err: any) {
      throw err;
    }
  };

export const saveTemplateForm =
  (templateId: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const agency = selectAgencyData(state);
    const templateForm = selectTemplateForm(state);
    const agentApiParams = selectAgentApiParams(state);

    const {
      maxEventSize,
      locationMeta,
      templateType,
      bookingAccounts,
      workingAssignments,
      distributionRule,
      capacity = [],
    } = templateForm;

    const isOrgTemplate = templateType === 'admin';

    const updatedTemplate = {
      ...templateForm,
      maxEventSize: maxEventSize === 0 ? 1 : maxEventSize,
      // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types '"phone_number" | "custom_location" | "meeting_link" | undefined' and '""' have no overlap.
      locationMeta: locationMeta.filter((location) => location.type !== ''),
      bookingAccounts: normalizeBookingAccounts(
        bookingAccounts,
        distributionRule,
      ),
      capacity: normalizeCapacity(capacity, distributionRule),
    } as const;

    const payload = {
      ...omit(updatedTemplate, [...TEMPLATE_FORM_PUT_BODY_OMIT_KEYS]),
      ...agentApiParams,
    } as const;
    const snakePayload = snake(payload, true, ['blocks', 'entityMap']);

    const path = isOrgTemplate
      ? `scheduler/organisational-template/${templateId}`
      : `scheduler/event-template/${templateId}`;

    try {
      const result = await dispatch(reduxApi.put(path, snakePayload));
      const camelizedResult = camel(result, true, ['blocks', 'entityMap']);

      const templateFormBeforeOverwrite = selectTemplateForm(getState());
      dispatch(setTemplateForm(camelizedResult));
      dispatch(updateTemplate(camelizedResult));
      // if org template, update the org template assignments and update the org template so that the template is updated in the org templates list
      if (isOrgTemplate && workingAssignments) {
        await dispatch(saveWorkingAssignments(templateId, workingAssignments));

        // update the org template so that the template is updated in the org templates list
        await dispatch(
          updateOrgTemplate({templateId, payload: camelizedResult}),
        );
      }

      trackTemplateSaveSuccess({
        templateFormBeforeOverwrite,
        agency,
      });

      setTemplateFormAnalyticsState(selectTemplateForm(getState()));
    } catch (error: any) {
      throw error;
    }
  };

export const addBookingAccountMember =
  (
    bookingAccountId: string,
    calendar?: SchedulerCalendar | null,
    agentSettings?: AgentSettings | null,
  ): ThunkAction =>
  // @ts-expect-error - TS2322 - Type '(dispatch: Dispatch, getState: GetState) => Promise<void>' is not assignable to type 'ThunkAction'.
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const templateForm = selectTemplateForm(state);

    const distributionRule = templateForm.distributionRule;

    const defaultBookingRow = {
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      start_time: '09:00',
      end_time: '17:00',
      rule_id: uniqueId(),
    } as const;

    let bookingAccountOfMember = {
      accountId: bookingAccountId,
      calendarId: calendar?.id,
      openBookingSlots: agentSettings?.businessHours.length
        ? agentSettings.businessHours // i.e. if _any_ hours have been set, default is []
        : // @ts-expect-error - TS2322 - Type '{ readonly days: readonly ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]; readonly start_time: "09:00"; readonly end_time: "17:00"; readonly rule_id: string; }' is not assignable to type 'BookingRow'.
          camel(bookingRowsAsBookingSlots([defaultBookingRow])),
      timezone: agentSettings?.timezone ?? 'America/Los_Angeles',
    };

    if (distributionRule === 'panel_interview') {
      bookingAccountOfMember = {
        ...bookingAccountOfMember,
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'isRequired' does not exist in type '{ accountId: string; calendarId: string | undefined; openBookingSlots: any; timezone: string; }'.
        isRequired: true,
        isHost: false,
        openBookingDates: [],
        bookingSlotType: 'week_based_slot',
      };
    }

    // Add the new booking account member to the template form
    const {bookingAccounts} = templateForm;
    const newBookingAccounts = [...bookingAccounts, bookingAccountOfMember];

    dispatch(
      updateTemplateFormField({
        field: 'bookingAccounts',
        value: newBookingAccounts,
      }),
    );

    return Promise.resolve();
  };

export const deleteBookingAccountMember =
  (bookingAccountId: string): ThunkAction =>
  // @ts-expect-error - TS2322 - Type '(dispatch: Dispatch, getState: GetState) => Promise<void>' is not assignable to type 'ThunkAction'.
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const templateForm = selectTemplateForm(state);

    const {bookingAccounts} = templateForm;

    const newBookingAccounts = bookingAccounts.filter(
      (bookingAccount) => bookingAccount.accountId !== bookingAccountId,
    );

    dispatch(
      updateTemplateFormField({
        field: 'bookingAccounts',
        value: newBookingAccounts,
      }),
    );

    return Promise.resolve();
  };

export const updateBookingAccountMember =
  (bookingAccountId: string, data: Partial<BookingAccount>): ThunkAction =>
  // @ts-expect-error - TS2322 - Type '(dispatch: Dispatch, getState: GetState) => Promise<void>' is not assignable to type 'ThunkAction'.
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const templateForm = selectTemplateForm(state);

    const {bookingAccounts} = templateForm;

    const newBookingAccounts = bookingAccounts.map((bookingAccount) => {
      if (bookingAccount.accountId === bookingAccountId) {
        return {...bookingAccount, ...data};
      }
      return bookingAccount;
    });

    dispatch(
      updateTemplateFormField({
        field: 'bookingAccounts',
        value: newBookingAccounts,
      }),
    );

    return Promise.resolve();
  };

export const setRoleForBookingAccountMember =
  (bookingAccountId: string, role: string): ThunkAction =>
  // @ts-expect-error - TS2322 - Type '(dispatch: Dispatch, getState: GetState) => Promise<void>' is not assignable to type 'ThunkAction'.
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const templateForm = selectTemplateForm(state);

    const {bookingAccounts} = templateForm;

    const newBookingAccounts = bookingAccounts.map((bookingAccount) => {
      if (role === 'host') {
        if (bookingAccount.accountId === bookingAccountId) {
          bookingAccount.isHost = true;
          bookingAccount.isRequired = true;
        } else {
          bookingAccount.isHost = false;
        }
      } else {
        if (bookingAccount.accountId === bookingAccountId) {
          if (role === 'optional') {
            bookingAccount.isRequired = false;
          } else if (role === 'required') {
            bookingAccount.isRequired = true;
          }
        }
      }
      return bookingAccount;
    });

    dispatch(
      updateTemplateFormField({
        field: 'bookingAccounts',
        value: newBookingAccounts,
      }),
    );

    return Promise.resolve();
  };

// @ts-expect-error - TS2322 - Type '(dispatch: Dispatch) => Promise<void | undefined>' is not assignable to type 'ThunkAction'.
export const resetTemplateForm = (): ThunkAction => (dispatch: Dispatch) => {
  dispatch(setTemplateForm(initialState));
  setTemplateFormAnalyticsState(null);
  return Promise.resolve();
};

// utility functions
export const parseTemplateForm = (
  payload: SchedulerTemplateForm,
): SchedulerTemplateForm => {
  const {maxEventSize, distributionRule, bookingAccounts} = payload || {};
  if (!maxEventSize || distributionRule !== MULTI_EVENT_TEMPLATE_TYPE) {
    return payload;
  }

  // Update booking accounts with maxEventSize where missing
  const parsedBookingAccounts = bookingAccounts.map((bookingAccount) => {
    const updatedAccount = {...bookingAccount};

    // Update openBookingSlots
    if (bookingAccount.openBookingSlots?.length) {
      updatedAccount.openBookingSlots = bookingAccount.openBookingSlots.map(
        (slot) => ({
          ...slot,
          maxEventSize: slot.maxEventSize ?? maxEventSize,
        }),
      );
    }

    // Update openBookingDates
    if (bookingAccount.openBookingDates?.length) {
      updatedAccount.openBookingDates = bookingAccount.openBookingDates.map(
        (date) => ({
          ...date,
          maxEventSize: date.maxEventSize ?? maxEventSize,
        }),
      );
    }

    // Update openBookingTimeBlocks
    if (bookingAccount.openBookingTimeBlocks?.length) {
      updatedAccount.openBookingTimeBlocks =
        bookingAccount.openBookingTimeBlocks.map((timeBlock) => ({
          ...timeBlock,
          maxEventSize: timeBlock.maxEventSize ?? maxEventSize,
        }));
    }

    return updatedAccount;
  });

  return {
    ...payload,
    bookingAccounts: parsedBookingAccounts,
  };
};
